import { IDefaultVOption } from "~/interfaces/vSelectInterfaces";
import { commonFilterMappingFormElements, FormElementTypesForMappings } from "~/models/mapping/mappingFormElementSupport";
import ModelMappingOrigin from "~/models/mapping/modelMappingOrigin";
import ModelMappingValueOrigin from "~/models/mapping/modelMappingValueOrigin";
import FormElement, { IFormElement } from "../../../formability/form_builder/models/formElements/formElement";
import FormElementCapabilityManager from "../../../formability/form_builder/models/formElements/formElementCapabilityManager";
import FormElementDefinition from "../../definitions/formElementDefinition";
import { FormElementType } from "../../enums/formElementType";
import {IOriginWrapper} from "~/interfaces/mappingInterfaces";
import OriginWrapperFormElementHelper from "~/models/mapping/originWrapperHelpers/originWrapperFormElementHelper";

interface IValue {
  label_title: string
  client_id: string
  labelSuffix: FormElementType
  section?: string
}

// When Repeatables and LookupFields (type License) are the Origin
export default class ModelMappingOriginFormElement extends ModelMappingOrigin<FormElement> {
  get origin(): FormElement {
    return FormElement.repo.find(this.id);
  }

  get identifier(): string {
    return this.origin?.client_id
  }

  get title(): string {
    return this.origin?.displayTitle
  }

  get values(): IValue[] {
    const values: IValue[] = []
    if (!this.origin) return values
    if (this.origin.is_repeatable) {
      const buildValues = (formElements: IFormElement[], moreAttrs: Partial<IValue> = {}, condition: (fe: IFormElement) => boolean = (fe) => true) => {
        const result: IValue[] = []
        for (let fe of formElements) {
          if (condition(fe)) {
            result.push({
              label_title: fe.defaultLabelTitle,
              client_id: fe.client_id,
              labelSuffix: fe.element_type,
              ...moreAttrs,
            })
          }
        }
        return result
      }
      const isNotRepeatable = (formElement: IFormElement) => formElement.is_repeatable !== true
      if (FormElementCapabilityManager.LAYOUT_CONTAINER_CONTROLS.includes(this.origin.element_type)) {
        const scopeRepeatableAncestorCount: number = this.origin.repeatableAncestors.length
        const formElements: IFormElement[] = this.origin.formElementsArrayDeep.filter(formElement => {
          // must not be repeatable and can't be contained in a repeatable inside this repeatable (differs from referenceableFormElementsDeep)
          return commonFilterMappingFormElements(formElement) && (formElement.repeatableAncestors.length - scopeRepeatableAncestorCount === 1)
        });
        values.push(...buildValues(formElements, {section: null}, isNotRepeatable))
      } else {
        values.push(...buildValues([this.origin]))
      }
      values.push(...buildValues(this.origin.getReferencableFormElements(), {section: 'Additional Form Fields'}, isNotRepeatable))
    }
    return values
  }

  searchValues(query?: string, options: { filters?: ((value: string) => boolean)[], showUnmappedOnly?: boolean } = {}): Partial<this['values']> {
    const filters = [value => value.label_title?.toLowerCase()?.trim()?.includes(query?.toLowerCase()?.trim())]
    if (options.showUnmappedOnly) {
      filters.unshift(value => {
        return !this.modelMapping.isMapped(value.client_id)
      })
    }
    return this.values.filter(value => filters.every(f => f(value)))
  }

  valueOptions(source: ModelMappingValueOrigin = null): IDefaultVOption[] {
    if (!this.origin?.is_repeatable) return []

    if (FormElementCapabilityManager.LAYOUT_CONTAINER_CONTROLS.includes(this.origin.element_type)) {
      return this.origin.formElementsArrayDeep.reduce((acc, fe) => {
        if (
          !fe.isTypeOneOf([FormElementType.uploadFiles, FormElementType.imageUpload]) &&
          fe.parent.element_type !== FormElementType.imageUpload &&
          fe.isTypeOneOf(FormElementTypesForMappings)
        ) {
          acc.push({
            label: fe.defaultLabelTitle || `Untitled ${FormElementDefinition.getDisplayName(fe.element_type)}`,
            value: fe.client_id,
            labelSuffix: FormElementDefinition.getDisplayName(fe.element_type),
          });
        }
        return acc;
      }, [])
    } else {
      return [{value: this.origin.client_id, label: this.origin.defaultLabelTitle, labelSuffix: FormElementDefinition.getDisplayName(this.origin.element_type)}]
    }
  }

  get uploaderValueOptions(): IDefaultVOption[] {
    if (!this.origin?.is_repeatable) return []
    return this.origin.formElementsArrayDeep.reduce((acc, fe) => {
      if (fe.isTypeOneOf([FormElementType.uploadFiles, FormElementType.imageUpload])) {
        acc.push({
          label: fe.defaultLabelTitle || `Untitled ${FormElementDefinition.getDisplayName(fe.element_type)}`,
          value: fe.client_id,
          labelSuffix: FormElementDefinition.getDisplayName(fe.element_type)
        });
      }
      return acc;
    }, []);
  }

  get isSingle(): boolean {
    if (!this.origin) return null
    return !this.origin.is_repeatable
  }

  mapValueToOption(value): IDefaultVOption {
    return {
      label: value.defaultLabelTitle || value.label_title,
      value: value.client_id,
      labelSuffix: `(${FormElementDefinition.getDisplayName(value.labelSuffix || this.origin.element_type)})`,
      section: value.section
    }
  }

  // ******************** NESTED MODEL MAPPING ******************** //
  get relationshipModels(): IFormElement[] {
    if (!this.hasOrigin) return []
    return this.origin.descendents.filter(element => {
      // filter descendents to repeatable elements where the nearest repeatable ancestor
      // is this origin (i.e. form element)
      // basically this finds all elements one-repeatable-down from the origin (form element)
      if (element.is_repeatable && element.repeatableAncestors[0] === this.origin) return true
      // lookup fields can be used in nested model mappings
      if (element.element_type === FormElementType.lookupField) return true
      return false
    })
  }

  nestedModelMappingSourceOptions(): IOriginWrapper[] {
    return this.relationshipModels.map(OriginWrapperFormElementHelper.build)
  }

  nestedModelMappingDestinationOptions(source: IOriginWrapper): IOriginWrapper[] {
    const allOptions = this.relationshipModels.map(OriginWrapperFormElementHelper.build)
    return this.modelMapping.modelMappingHandler.destinationOptionsForSource(allOptions, source)
  }
  // ******************** NESTED MODEL MAPPING END ******************** //
}